<script setup>
  const { isUserStatusValid } = useAuth()
  const { openModal: openSearchModal } = useGlobalSearch()

  const mainMenuRef = ref(null)
  const headerRef = ref(null)
  const isScrolled = ref(false)

  const localePath = useLocalePath()
  const { locale } = useI18n()
  const { menuConfig } = useMainMenu()

  const updateHeaderHeight = () => {
    if (headerRef.value) {
      const height = headerRef.value.offsetHeight
      document.documentElement.style.setProperty('--header-height', `${height}px`)
    }
  }
  const authStore = useAuthStore()
  const user = computed(() => authStore.user)
  const { userData, isIndividuel } = useAuthData()

  const handleScroll = () => {
    if (window.scrollY > 100) {
      isScrolled.value = true
    } else if (window.scrollY === 0) {
      isScrolled.value = false
    }
  }

  const onFocusIn = (index) => {
    const el = mainMenuRef.value?.elementsWithDropdown?.[index]
    if (el) {
      mainMenuRef.value?.openDropdown(el)
    }
  }

  const onFocusOut = () => {
    mainMenuRef.value?.closeDropdown()
  }

  const onLinkClick = () => {
    mainMenuRef.value?.closeDropdown()
  }

  const getUserStatusValid = computed(() => isUserStatusValid(userData.value))

  onMounted(() => {
    window.addEventListener('scroll', handleScroll)
    window.addEventListener('resize', updateHeaderHeight)
    handleScroll()
    updateHeaderHeight()
  })

  onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll)
    window.removeEventListener('resize', updateHeaderHeight)
  })

  watch(isScrolled, () => {
    setTimeout(updateHeaderHeight, 300)
  })
</script>

<template>
  <div ref="headerRef" class="w-full bg-white border-b border-slate-200 relative z-[1100]">
    <div class="container flex items-end justify-between">
      <div class="flex">
        <NuxtLink :to="localePath({ name: 'index' })" class="flex items-center">
          <span class="sr-only">UIA</span>
          <picture>
            <source srcset="/logo/logo_multi_blue.webp" type="image/webp" />
            <img
              :class="[
                'w-auto object-contain transition-all duration-300 mx-2 xl:mx-0',
                isScrolled ? 'h-8 lg:h-16 my-2' : 'h-12 lg:h-20 my-4'
              ]"
              src="@/public/logo/logo_multi_blue.png"
              alt="Company Logo"
            />
          </picture>
        </NuxtLink>
      </div>
      <div class="flex items-center justify-end w-full">
        <div class="hidden lg:flex items-center justify-end">
          <div class="self-end">
            <CommonDropdownMenu
              ref="mainMenuRef"
              :menu="menuConfig"
              :locale="locale"
              class="font-semibold"
              @click.capture="onLinkClick"
            >
              <template #default="{ item, index }">
                <client-only>
                  <component
                    :is="item.component"
                    v-bind="{ dropdown: item.dropdown }"
                    @focus="onFocusIn(index)"
                    @blur="onFocusOut"
                  />
                </client-only>
              </template>
            </CommonDropdownMenu>
          </div>
        </div>
        <div class="flex gap-4">
          <button
            :aria-label="$t('globalSearch.placeholder')"
            :class="[
              'hidden lg:flex items-center justify-center w-9 h-9 rounded-full text-slate-500 hover:text-sky-700 hover:bg-sky-50 transition-all duration-200 cursor-pointer self-end',
              isScrolled ? 'mb-2 lg:mb-[15px]' : 'mb-4 lg:mb-[15px]'
            ]"
            @click="openSearchModal"
          >
            <font-awesome class="text-lg" :icon="['fas', 'magnifying-glass']" />
          </button>
          <ClientOnly>
            <NuxtLink
              v-if="!user"
              :to="localePath({ name: 'auth-choose' })"
              :class="[
                'self-end transition-all duration-300 rounded-full bg-sky-900 hover:bg-sky-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:shadow-lg focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-slate-800 group flex items-center w-fit overflow-hidden',
                isScrolled ? 'mb-2 lg:mb-[15px]' : 'mb-4 lg:mb-[15px]'
              ]"
            >
              <span class="mr-2 whitespace-nowrap flex items-center">
                <span>{{ $t('menu.mainMenu.createAccountShort') }}</span>
                <span
                  class="inline-block max-w-0 opacity-0 overflow-hidden transition-all duration-300 ease-in-out group-hover:max-w-[200px] group-hover:opacity-100 group-hover:ml-1"
                >
                  {{ $t('menu.mainMenu.createAccountHover') }}
                </span>
              </span>
              <font-awesome
                :icon="['fas', 'chevron-right']"
                class="transition-transform duration-300 group-hover:translate-x-1"
              />
            </NuxtLink>
            <NuxtLink
              v-if="user && !getUserStatusValid && isIndividuel"
              :to="localePath({ name: 'auth-subscribe' })"
              :class="[
                'self-end transition-all duration-300 rounded-full bg-sky-900 hover:bg-sky-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:shadow-lg focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-slate-800 group flex items-center w-fit overflow-hidden',
                isScrolled ? 'mb-2 lg:mb-[15px]' : 'mb-4 lg:mb-[15px]'
              ]"
            >
              <span class="mr-2 whitespace-nowrap flex items-center">
                <span>{{ $t('menu.mainMenu.membershipShort') }}</span>
                <span
                  class="inline-block max-w-0 opacity-0 overflow-hidden transition-all duration-300 ease-in-out group-hover:max-w-[200px] group-hover:opacity-100 group-hover:ml-1"
                >
                  {{ $t('menu.mainMenu.membershipHover') }}
                </span>
              </span>
              <font-awesome
                :icon="['fas', 'chevron-right']"
                class="transition-transform duration-300 group-hover:translate-x-1"
              />
            </NuxtLink>
          </ClientOnly>
        </div>
        <MenuBurger :class="['lg:hidden ml-3 self-end transition-all duration-300', isScrolled ? 'mb-2' : 'mb-4']" />
      </div>
    </div>
  </div>
</template>
